import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Button,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CloseIcon from '@mui/icons-material/Close';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { employeeApi, TeamMember, PendingInvitation } from '../../services/employeeService';
import { useSalonContextStore } from '../../store/salonContextStore';
import { useAuthStore } from '../../store/authStore';
import {
  PageHeader,
  SectionCard,
  EmptyState,
  LoadingState,
  StatusPill,
  PersonAvatar,
  FieldGrid,
  useConfirm,
} from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatDate } from '../../utils/format';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Administrator systemu',
  SALON_OWNER: 'Właściciel',
  MANAGER: 'Menedżer',
  STAFF: 'Pracownik',
  ACCOUNTANT: 'Księgowy',
  ANALYST: 'Analityk',
  MARKETER: 'Marketer',
  IT: 'Informatyk',
  CLIENT: 'Klient',
};

const ASSIGNABLE_ROLES = ['STAFF', 'MANAGER', 'ACCOUNTANT', 'ANALYST', 'MARKETER', 'IT'];
const SCOPED_ROLES = ['MANAGER', 'ACCOUNTANT', 'ANALYST', 'MARKETER', 'IT'];

// Positions offered when adding a user → mapped to a system role.
// "Wspólnik" and "Administrator" get manager-level access (per owner's choice).
const POSITIONS: { label: string; role: string; hint?: string }[] = [
  { label: 'Wspólnik', role: 'MANAGER', hint: 'pełny dostęp operacyjny (menedżer)' },
  { label: 'Administrator', role: 'MANAGER', hint: 'pełny dostęp operacyjny (menedżer)' },
  { label: 'Menedżer', role: 'MANAGER' },
  { label: 'Księgowy', role: 'ACCOUNTANT', hint: 'rozliczenia, płace, eksporty' },
  { label: 'Analityk', role: 'ANALYST', hint: 'podgląd, statystyki, rentowność' },
  { label: 'Marketer', role: 'MARKETER', hint: 'klienci, marketing, kampanie' },
  { label: 'Informatyk', role: 'IT', hint: 'ustawienia i konfiguracja' },
  { label: 'Pracownik', role: 'STAFF', hint: 'swój grafik i czas pracy' },
];

const nameOf = (p: { firstName?: string | null; lastName?: string | null }) =>
  [p.firstName, p.lastName].filter(Boolean).join(' ');

const PersonCell: React.FC<{
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  seed: string;
  badges?: React.ReactNode;
}> = ({ firstName, lastName, email, seed, badges }) => {
  const name = nameOf({ firstName, lastName });
  return (
    <Stack direction="row" gap={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <PersonAvatar firstName={firstName} lastName={lastName} fallback={email} seed={seed} size={36} />
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
          <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{name || email}</Typography>
          {badges}
        </Stack>
        {name && <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{email}</Typography>}
      </Box>
    </Stack>
  );
};

export const SalonUsersPage: React.FC = () => {
  const { activeSalonId, salons } = useSalonContextStore();
  const { user } = useAuthStore();
  const confirm = useConfirm();
  const salonId = activeSalonId || salons[0]?.id || null;

  const [team, setTeam] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [busyInvite, setBusyInvite] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [pwTarget, setPwTarget] = useState<TeamMember | null>(null);
  const [pwValue, setPwValue] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ firstName: '', lastName: '', email: '', phone: '', positionIdx: 0 });
  const [addSaving, setAddSaving] = useState(false);

  const load = useCallback(async () => {
    if (!salonId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [t, inv] = await Promise.all([employeeApi.team(salonId), employeeApi.invitations(salonId)]);
      setTeam(t);
      setInvitations(inv);
    } catch {
      setToast({ msg: 'Nie udało się załadować zespołu. Odśwież stronę, aby spróbować ponownie.', kind: 'error' });
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  useEffect(() => {
    load();
  }, [load]);

  const err = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

  const changeRole = async (m: TeamMember, role: string) => {
    if (!salonId || role === m.role) return;
    setBusyUser(m.userId);
    try {
      await employeeApi.setRole(m.userId, salonId, role);
      setToast({ msg: `Zmieniono rolę: ${m.email}`, kind: 'success' });
      await load();
    } catch (e) {
      setToast({ msg: err(e, 'Nie udało się zmienić roli'), kind: 'error' });
    } finally {
      setBusyUser(null);
    }
  };

  const toggleLocationAccess = async (m: TeamMember, next: boolean) => {
    if (!salonId) return;
    if (!next) {
      const ok = await confirm({
        title: 'Odebrać dostęp do tej lokalizacji?',
        details: (
          <>
            <b>{nameOf(m) || m.email}</b>
            {nameOf(m) && <> · {m.email}</>}
            <br />
            {ROLE_LABELS[m.role] || m.role}
          </>
        ),
        description: 'Ta osoba przestanie widzieć dane tej lokalizacji. Dostęp możesz przywrócić w każdej chwili.',
        confirmLabel: 'Odbierz dostęp',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusyUser(m.userId);
    try {
      if (next) await employeeApi.assignManager(m.userId, salonId);
      else await employeeApi.removeManager(m.userId, salonId);
      await load();
    } catch (e) {
      setToast({ msg: err(e, 'Nie udało się zmienić dostępu'), kind: 'error' });
    } finally {
      setBusyUser(null);
    }
  };

  const savePassword = async () => {
    if (!salonId || !pwTarget) return;
    setPwSaving(true);
    try {
      await employeeApi.setPassword(pwTarget.userId, salonId, pwValue);
      setToast({ msg: `Ustawiono hasło: ${pwTarget.email}`, kind: 'success' });
      setPwTarget(null);
      setPwValue('');
    } catch (e) {
      setToast({ msg: err(e, 'Nie udało się ustawić hasła'), kind: 'error' });
    } finally {
      setPwSaving(false);
    }
  };

  const inviteUser = async () => {
    if (!salonId) return;
    const pos = POSITIONS[addForm.positionIdx];
    setAddSaving(true);
    try {
      await employeeApi.invite({
        salonId,
        email: addForm.email.trim(),
        firstName: addForm.firstName.trim(),
        lastName: addForm.lastName.trim(),
        phone: addForm.phone.trim() || undefined,
        role: pos.role,
      });
      setToast({ msg: `Zaproszenie wysłane: ${addForm.email}`, kind: 'success' });
      setAddOpen(false);
      setAddForm({ firstName: '', lastName: '', email: '', phone: '', positionIdx: 0 });
      await load();
    } catch (e) {
      setToast({ msg: err(e, 'Nie udało się wysłać zaproszenia'), kind: 'error' });
    } finally {
      setAddSaving(false);
    }
  };

  const resendInvite = async (inv: PendingInvitation) => {
    setBusyInvite(inv.id);
    try {
      await employeeApi.resendInvitation(inv.id);
      setToast({ msg: `Zaproszenie wysłane ponownie: ${inv.email}`, kind: 'success' });
      await load();
    } catch (e) {
      setToast({ msg: err(e, 'Nie udało się wysłać ponownie'), kind: 'error' });
    } finally {
      setBusyInvite(null);
    }
  };

  const cancelInvite = async (inv: PendingInvitation) => {
    const ok = await confirm({
      title: 'Anulować zaproszenie?',
      details: (
        <>
          <b>{nameOf(inv) || inv.email}</b>
          {nameOf(inv) && <> · {inv.email}</>}
        </>
      ),
      description: 'Link z wysłanego e-maila przestanie działać. Jeśli zmienisz zdanie, wyślij nowe zaproszenie.',
      confirmLabel: 'Anuluj zaproszenie',
      tone: 'danger',
    });
    if (!ok) return;
    setBusyInvite(inv.id);
    try {
      await employeeApi.cancelInvitation(inv.id);
      setToast({ msg: `Zaproszenie anulowane: ${inv.email}`, kind: 'success' });
      await load();
    } catch (e) {
      setToast({ msg: err(e, 'Nie udało się anulować'), kind: 'error' });
    } finally {
      setBusyInvite(null);
    }
  };

  const pwValid = pwValue.length >= 8 && /[A-Z]/.test(pwValue) && /[0-9]/.test(pwValue);
  const emailValid = /.+@.+\..+/.test(addForm.email);
  const addValid = !!(addForm.firstName.trim() && addForm.lastName.trim() && emailValid);
  const closeAdd = () => setAddOpen(false);
  // Szkielet tylko przy pierwszym wczytaniu — po akcjach (zmiana roli, zaproszenie) tabela nie mruga.
  const initialLoading = loading && team.length === 0 && invitations.length === 0;

  const inviteButton = (
    <Button variant="contained" startIcon={<PersonAddAltIcon />} onClick={() => setAddOpen(true)} disabled={!salonId}>
      Zaproś użytkownika
    </Button>
  );

  if (!salonId) {
    return (
      <Box>
        <PageHeader breadcrumbs={[{ label: 'Ustawienia salonu' }]} title="Użytkownicy i role" description="Zapraszaj osoby, przydzielaj role i dostęp do lokalizacji." />
        <Paper>
          <EmptyState icon={<StorefrontOutlinedIcon />} title="Nie wybrano salonu" description="Wybierz salon w górnym pasku, aby zarządzać zespołem." />
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Ustawienia salonu' }]}
        title="Użytkownicy i role"
        description={
          team.length > 0
            ? `${countLabel(team.length, 'osoba', 'osoby', 'osób')} z dostępem do salonu${invitations.length ? ` · ${countLabel(invitations.length, 'zaproszenie', 'zaproszenia', 'zaproszeń')} w toku` : ''}`
            : 'Zapraszaj osoby, przydzielaj role i dostęp do lokalizacji.'
        }
        actions={inviteButton}
      />

      <Stack gap={2.5}>
        {initialLoading ? (
          <LoadingState variant="table" rows={4} />
        ) : team.length === 0 ? (
          <Paper>
            <EmptyState
              icon={<GroupOutlinedIcon />}
              title="Brak członków zespołu w tej lokalizacji"
              description="Zaproś pierwszą osobę — dostanie e-mail z linkiem do ustawienia hasła."
              actions={inviteButton}
            />
          </Paper>
        ) : (
          <SectionCard title="Zespół" subtitle="Rola decyduje, co dana osoba widzi i może zmieniać w salonie." padding="none">
            <TableContainer>
              <Table sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Osoba</TableCell>
                    <TableCell sx={{ width: 210 }}>Rola</TableCell>
                    <TableCell sx={{ width: 200 }}>Dostęp do lokalizacji</TableCell>
                    <TableCell align="right" sx={{ width: 150 }}>Akcje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {team.map((m) => {
                    const busy = busyUser === m.userId;
                    return (
                      <TableRow key={m.userId} hover>
                        <TableCell>
                          <PersonCell
                            firstName={m.firstName}
                            lastName={m.lastName}
                            email={m.email}
                            seed={m.userId}
                            badges={
                              <>
                                {user?.id === m.userId && <StatusPill label="Ty" tone="plum" size="small" noDot />}
                                {!m.isActive && <StatusPill label="Konto nieaktywne" tone="neutral" size="small" />}
                              </>
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {m.isOwner ? (
                            <StatusPill label={ROLE_LABELS[m.role] || m.role} tone="plum" noDot />
                          ) : (
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                              <Select
                                value={ASSIGNABLE_ROLES.includes(m.role) ? m.role : 'STAFF'}
                                onChange={(e) => changeRole(m, e.target.value)}
                                disabled={busy}
                                inputProps={{ 'aria-label': `Rola: ${nameOf(m) || m.email}` }}
                              >
                                {ASSIGNABLE_ROLES.map((r) => (
                                  <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell>
                          {m.isOwner ? (
                            <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft }}>Pełny dostęp</Typography>
                          ) : SCOPED_ROLES.includes(m.role) ? (
                            <Tooltip title="Dostęp menedżera, księgowego lub analityka do danych tej lokalizacji">
                              <Stack direction="row" alignItems="center" gap={1} sx={{ width: 'fit-content' }}>
                                <Switch
                                  checked={m.isManagerHere}
                                  onChange={(e) => toggleLocationAccess(m, e.target.checked)}
                                  disabled={busy}
                                  size="small"
                                  inputProps={{ 'aria-label': `Dostęp do lokalizacji: ${nameOf(m) || m.email}` }}
                                />
                                <Typography sx={{ fontSize: '0.875rem', color: m.isManagerHere ? tokens.ink : tokens.muted }}>
                                  {m.isManagerHere ? 'Ma dostęp' : 'Brak dostępu'}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '0.875rem', color: tokens.muted }}>Według grafiku</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {!m.isOwner && (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<KeyOutlinedIcon />}
                              onClick={() => { setPwTarget(m); setPwValue(''); }}
                              disabled={busy}
                            >
                              Ustaw hasło
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        )}

        {!initialLoading && invitations.length > 0 && (
          <SectionCard title="Zaproszenia" subtitle="Osoby, które nie aktywowały jeszcze konta." padding="none">
            <TableContainer>
              <Table sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Osoba</TableCell>
                    <TableCell sx={{ width: 150 }}>Rola</TableCell>
                    <TableCell sx={{ width: 200 }}>Status</TableCell>
                    <TableCell align="right" sx={{ width: 300 }}>Akcje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((inv) => {
                    const busy = busyInvite === inv.id;
                    return (
                      <TableRow key={inv.id} hover>
                        <TableCell>
                          <PersonCell firstName={inv.firstName} lastName={inv.lastName} email={inv.email} seed={inv.email} />
                        </TableCell>
                        <TableCell>
                          <StatusPill label={ROLE_LABELS[inv.role] || inv.role} tone="neutral" noDot />
                        </TableCell>
                        <TableCell>
                          <StatusPill label={inv.expired ? 'Wygasło' : 'Czeka na akceptację'} tone={inv.expired ? 'danger' : 'warning'} />
                          <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                            {inv.expired ? `Wygasło ${formatDate(inv.expiresAt)}` : `Ważne do ${formatDate(inv.expiresAt)}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" gap={0.5} justifyContent="flex-end" flexWrap="wrap">
                            <Button size="small" variant="text" startIcon={<SendOutlinedIcon />} onClick={() => resendInvite(inv)} disabled={busy}>
                              Wyślij ponownie
                            </Button>
                            <Button size="small" variant="text" color="error" startIcon={<CloseIcon />} onClick={() => cancelInvite(inv)} disabled={busy}>
                              Anuluj
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        )}
      </Stack>

      {/* Add user dialog */}
      <Dialog open={addOpen} onClose={closeAdd} fullWidth maxWidth="sm" aria-labelledby="invite-user-title">
        <DialogTitle id="invite-user-title">Zaproś użytkownika</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 0.5 }}>
            <FieldGrid columns={2}>
              <TextField label="Imię" required fullWidth autoFocus value={addForm.firstName} onChange={(e) => setAddForm((f) => ({ ...f, firstName: e.target.value }))} />
              <TextField label="Nazwisko" required fullWidth value={addForm.lastName} onChange={(e) => setAddForm((f) => ({ ...f, lastName: e.target.value }))} />
            </FieldGrid>
            <TextField
              label="E-mail"
              type="email"
              required
              fullWidth
              value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
              error={addForm.email.length > 0 && !emailValid}
              helperText={addForm.email.length > 0 && !emailValid ? 'Podaj pełny adres e-mail, np. anna@salon.pl' : 'Na ten adres wyślemy zaproszenie.'}
            />
            <TextField label="Telefon (opcjonalnie)" fullWidth value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))} />
            <TextField
              select
              label="Rola / stanowisko"
              fullWidth
              value={addForm.positionIdx}
              onChange={(e) => setAddForm((f) => ({ ...f, positionIdx: Number(e.target.value) }))}
              SelectProps={{ renderValue: (v) => POSITIONS[Number(v)]?.label }}
              helperText={POSITIONS[addForm.positionIdx]?.hint ? `Dostęp: ${POSITIONS[addForm.positionIdx].hint}` : undefined}
            >
              {POSITIONS.map((p, i) => (
                <MenuItem key={p.label} value={i} sx={{ display: 'block', py: 1 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{p.label}</Typography>
                  {p.hint && <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{p.hint}</Typography>}
                </MenuItem>
              ))}
            </TextField>
            <Alert severity="info" icon={<MailOutlineIcon fontSize="inherit" />}>
              Osoba otrzyma e-mail z zaproszeniem. Sama ustawi hasło i potwierdzi adres kodem, aby aktywować konto.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeAdd}>Anuluj</Button>
          <Button variant="contained" onClick={inviteUser} disabled={addSaving || !addValid}>
            {addSaving ? 'Wysyłanie…' : 'Wyślij zaproszenie'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set password dialog */}
      <Dialog open={!!pwTarget} onClose={() => setPwTarget(null)} fullWidth maxWidth="xs" aria-labelledby="set-password-title">
        <DialogTitle id="set-password-title">Ustawić nowe hasło?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: tokens.inkSoft, mb: 2 }}>
            Nowe hasło dla <b>{pwTarget?.email}</b>. Przekaż je tej osobie bezpiecznym kanałem.
          </Typography>
          <TextField
            label="Nowe hasło"
            type="text"
            fullWidth
            autoFocus
            value={pwValue}
            onChange={(e) => setPwValue(e.target.value)}
            error={pwValue.length > 0 && !pwValid}
            helperText="Min. 8 znaków, w tym 1 wielka litera i 1 cyfra."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setPwTarget(null)}>Anuluj</Button>
          <Button variant="contained" onClick={savePassword} disabled={pwSaving || !pwValid}>
            {pwSaving ? 'Zapisywanie…' : 'Ustaw hasło'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? (
          <Alert severity={toast.kind} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default SalonUsersPage;
